import { Check, ChevronDown, Pause, Play, SlidersHorizontal, Sparkles, WandSparkles } from "lucide-react";
import type { StyleState } from "../preview-bridge";

export function MotionPanel({
  styles, paused, advanced, editable, onPause, onAdvanced, onStyle, onAskAssistant,
}: {
  styles: StyleState;
  paused: boolean;
  advanced: boolean;
  /** false when the bundle declares no motion tokens for these controls to move */
  editable: boolean;
  onPause: () => void;
  onAdvanced: () => void;
  onStyle: (token: string, value: string) => void;
  /** omitted while the assistant engine is unavailable */
  onAskAssistant?: () => void;
}) {
  if (!editable) {
    return (
      <div className="motion-panel">
        <div className="motion-head"><div><h3>Motion</h3><p>Set the energy level across the site.</p></div><button onClick={onPause}>{paused ? <Play size={14} /> : <Pause size={14} />}{paused ? "Play preview" : "Pause preview"}</button></div>
        <div className="styles-unavailable">
          <WandSparkles size={18} />
          <strong>This site's motion isn't token-based</strong>
          <p>Its animation is authored into the design rather than exposed as adjustable tokens, so these controls can't reach it. You can still pause the preview above.</p>
          {onAskAssistant && <button className="editor-button primary" onClick={onAskAssistant}>Ask ProtoPal</button>}
        </div>
        <div className="motion-note"><Sparkles size={15} /><span>Visitors who prefer reduced motion will always see a still version.</span></div>
      </div>
    );
  }
  return (
    <div className="motion-panel">
      <div className="motion-head"><div><h3>Motion</h3><p>Set the energy level across the site.</p></div><button onClick={onPause}>{paused ? <Play size={14} /> : <Pause size={14} />}{paused ? "Play preview" : "Pause preview"}</button></div>
      <div className="motion-options">{[
        ["none", "Still", "No decorative movement"],
        ["subtle", "Subtle", "Gentle entrances and feedback"],
        ["expressive", "Expressive", "More energy, still disciplined"],
      ].map(([value, label, copy]) => <button key={value} className={styles.motion === value ? "active" : ""} onClick={() => onStyle("motion", value)}><span className={`motion-sample ${value}`}><i /><i /><i /></span><span><strong>{label}</strong><small>{copy}</small></span>{styles.motion === value && <Check size={15} />}</button>)}</div>
      <button className="advanced-toggle" onClick={onAdvanced} aria-expanded={advanced}><SlidersHorizontal size={14} /> Advanced controls <ChevronDown size={14} /></button>
      {advanced && <div className="advanced-motion"><label className="range-label"><span>Duration <output>{styles["motion.duration"]}ms</output></span><input type="range" min="120" max="900" step="20" value={styles["motion.duration"]} onChange={(event) => onStyle("motion.duration", event.target.value)} /></label><label className="field-label">Easing<select value={styles["motion.easing"]} onChange={(event) => onStyle("motion.easing", event.target.value)}>{["linear", "ease", "ease-in", "ease-out", "ease-in-out", "spring"].map((value) => <option key={value}>{value}</option>)}</select></label><label className="range-label"><span>Stagger <output>{styles["motion.stagger"]}ms</output></span><input type="range" min="0" max="500" step="10" value={styles["motion.stagger"]} onChange={(event) => onStyle("motion.stagger", event.target.value)} /></label><p>Controls are intentionally bounded to keep motion comfortable and production-safe.</p></div>}
      <div className="motion-note"><Sparkles size={15} /><span>Visitors who prefer reduced motion will always see a still version.</span></div>
    </div>
  );
}
